import { Button, cn, Skeleton } from '@heroui/react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import aiAnalytics from 'assets/images/aiAnalytics.png'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { useAuth } from 'hooks/useAuth'
import useCallLogsTable from 'hooks/useCallLogsTable'
import { useCallLogsPagination } from 'hooks/useCallsPagination'
import { ArrowRight, SearchX } from 'lucide-react'
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSetState } from 'react-use'
import { $fetch } from 'utils/fetch'
import { fetchCallLogs } from 'utils/funcs/call-logs/callLogsQueries'
import { convertFiltersToParams } from 'utils/funcs/call-logs/filterFns'
import CallDetailsSlideOut from '../CallLogs/CallDetailsSlideOut'
import CallLogsTable from '../CallLogs/CallLogsTable'
import ResizableDrawer from '../CallLogs/ResizeableDrawer'
import FilterHeader from './components/FilterHeader'
import MegaChart from './components/MegaChart'
import PieChart from './components/PieChart'
import { presets } from './utils/rangePresets'

function EmptyState({ type, isFetching }) {
  if (isFetching) {
    return (
      <Skeleton className={cn('w-full', type === 'call logs' ? 'h-96' : 'h-60')} />
    )
  }

  return (
    <div className="flex h-64 w-full flex-col items-center justify-center gap-2">
      <p className="text-small text-default-500">
        No
        {' '}
        {type}
        {' '}
        found
      </p>
      <SearchX size={16} className="text-default-500" />
    </div>
  )
}

export default function CallAnalytics() {
  const { callId } = useParams()
  const navigate = useNavigate()
  const { take: callLogsTake } = useCallLogsPagination()
  const { org } = useAuth()

  const isEnterprise = useMemo(() => {
    return org?.org_type === 'enterprise'
  }, [org])

  const take = useMemo(() => {
    return Math.max(callLogsTake - 25, 5)
  }, [callLogsTake])

  const [filters, setFilters] = useSetState({
    ...presets.lastWeek,
    direction: 'all',
  })

  const callLogFilters = useMemo(() => {
    return [
      { field: 'created_at', operator: 'lte', value: filters.end.toString() },
      { field: 'created_at', operator: 'gte', value: filters.start.toString() },
      ...(filters.pathwayId ? [{ field: 'pathway_id', operator: 'equals', value: filters.pathwayId }] : []),
      ...(filters.batchId ? [{ field: 'batch_id', operator: 'equals', value: filters.batchId }] : []),
      ...(filters.direction !== 'all' ? [{ field: 'inbound', operator: 'equals', value: String(filters.direction === 'inbound') }] : []),
      ...(filters.pathwayTag ? [{ field: 'pathway_tags', operator: 'hasSome', value: filters.pathwayTag }] : []),
      ...(filters.to ? [{ field: 'to', operator: 'equals', value: filters.to }] : []),
      ...(filters.from ? [{ field: 'from', operator: 'equals', value: filters.from }] : []),
    ]
  }, [filters])

  const pieChartParams = useMemo(() => {
    return {
      start: filters.start.toString(),
      end: filters.end.toString(),
      ...(filters.pathwayId ? { pathway_id: filters.pathwayId } : {}),
      ...(filters.batchId ? { batch_id: filters.batchId } : {}),
      ...(filters.direction !== 'all' ? { inbound: filters.direction === 'inbound' } : {}),
      ...(filters.to ? { to: filters.to } : {}),
      ...(filters.from ? { from: filters.from } : {}),
    }
  }, [filters])

  const graphParams = useMemo(() => {
    return {
      ...pieChartParams,
      ...(filters.pathwayTag ? { pathway_tag: filters.pathwayTag } : {}),
    }
  }, [pieChartParams, filters])

  const { data: { data: graphData } = {}, isLoading: isLoadingGraphData, isFetching: isFetchingGraphData } = useQuery({
    queryKey: ['call-metrics', graphParams],
    queryFn: () => $fetch('/v2/analytics/calls', {
      params: graphParams,
    }),
    staleTime: 1000 * 60 * 10, // 10 minutes
    placeholderData: keepPreviousData,
  })

  const { data: { data: pieChartData } = {}, isLoading: isLoadingPieData, isFetching: isFetchingPieData } = useQuery({
    queryKey: ['ai-metrics', pieChartParams],
    queryFn: () => $fetch('/v2/analytics/ai', {
      params: pieChartParams,
    }),
    staleTime: 1000 * 60 * 10, // 10 minutes
    placeholderData: keepPreviousData,
    enabled: isEnterprise,
  })

  const {
    data: { calls } = {},
    isLoading: isLoadingCallLogs,
    isFetching: isFetchingCallLogs,
  } = useQuery({
    queryKey: ['callLogs', take, callLogFilters, 'AND', 'created_at', 'desc'],
    queryFn: fetchCallLogs,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 3, // 5 minutes
    meta: {
      errorMessage: 'Error fetching call logs',
    },
  })

  const table = useCallLogsTable({
    data: calls,
  })

  const handleViewAll = () => {
    const newSearchParams = convertFiltersToParams(callLogFilters)
    const queryString = newSearchParams.toString()
    navigate(`/dashboard/call-logs?${queryString}`)
  }

  return (
    <div className="flex h-full w-full flex-col gap-3 p-5">
      {isLoadingGraphData
        ? (
            <div className="flex h-full flex-col items-center justify-center">
              <GradientLoadingAnimation
                message="Loading Call Analytics"
                variant="roseGarden"
                rounded="full"
                gray
              />
            </div>
          )
        : (
            <>
              <FilterHeader filters={filters} setFilters={setFilters} tags={pieChartData?.tags} />

              <MegaChart analyticsData={graphData} isFetching={isFetchingGraphData} />

              {isEnterprise
                ? (
                    <dl className="flex gap-3">
                      {isLoadingPieData
                        ? (
                            <>
                              <Skeleton className="h-60 w-full" />
                              <Skeleton className="h-60 w-full" />
                              <Skeleton className="h-60 w-full" />
                            </>
                          )
                        : (
                            <>
                              {Object.entries(pieChartData ?? {}).map(([key, value]) => (
                                <React.Fragment key={key}>
                                  {value.length > 0
                                    ? (
                                        <PieChart data={value} type={key} activeTag={filters.pathwayTag} isFetching={isFetchingPieData} />
                                      )
                                    : (
                                        <EmptyState type={key} isFetching={isFetchingPieData} />
                                      )}
                                </React.Fragment>
                              ))}
                            </>
                          )}
                    </dl>
                  )
                : (
                    <div className={`
                      relative min-h-72 w-full max-w-full overflow-hidden
                    `}
                    >
                      <img
                        src={aiAnalytics}
                        alt="AI Analytics"
                        className={`
                          h-full w-full object-contain opacity-90 blur-md
                        `}
                      />
                      <div className={`
                        absolute top-0 left-0 flex h-full w-full flex-col
                        items-center justify-center
                      `}
                      >
                        <div className={`
                          flex flex-col gap-4 rounded-lg border-2 bg-white/70
                          p-4
                        `}
                        >
                          <p className="text-small">
                            <strong>Advanced Analytics</strong>
                            {' '}
                            available for Enterprise plans
                          </p>
                          <Button color="primary" onPress={() => navigate('/enterprise')} size="sm" radius="sm">
                            Contact Sales
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

              {isLoadingCallLogs
                ? (
                    <Skeleton className="mt-8 h-96 w-full" />
                  )
                : calls?.length > 0
                  ? (
                      <div className="flex w-full flex-col gap-3 pb-8">
                        <CallLogsTable
                          table={table}
                          isRefreshing={isFetchingCallLogs}
                        />
                        <Button
                          onPress={handleViewAll}
                          variant="flat"
                          size="sm"
                          className="ml-auto"
                        >
                          View all
                          <ArrowRight size={16} />
                        </Button>
                      </div>
                    )
                  : (
                      <EmptyState type="call logs" isFetching={isFetchingCallLogs} />
                    )}

              {!!callId && (
                <ResizableDrawer>
                  <CallDetailsSlideOut
                    callId={callId}
                    onClose={() => table.setRowSelection(null)}
                  />
                </ResizableDrawer>
              )}
            </>
          )}
    </div>
  )
}
